/* eslint-disable multiline-ternary */
import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  IconButton,
  Icon,
  Flex,
  Heading,
  Spinner,
  SimpleGrid,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useRole } from '../../providers/RoleProvider'
import { useAccount } from '../../client/modules/account/AccountProvider'
import {
  dateKeyInTimeZone,
  normalizeAppointmentSettings
} from '../../utils/appointmentUtils'
import {
  getReport,
  type Report
} from '../../services/reporting/ReportingService'
export default function ReportSummary({
  family
}: {
  family: 'analytics' | 'finance'
}) {
  const { doctorId } = useRole()
  const { appointmentSettings } = useAccount()
  const { formatMessage, locale } = useIntl()
  const t = (key: string) =>
    formatMessage({ id: `reports.${key}`, defaultMessage: key })
  const day = dateKeyInTimeZone(
    Date.now(),
    normalizeAppointmentSettings(appointmentSettings).timezone
  )
  const monthEnd = new Date(
    Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)), 0)
  )
    .toISOString()
    .slice(0, 10)
  const [report, setReport] = useState<Report | null>(null)
  const [todayReport, setTodayReport] = useState<Report | null>(null)
  const [error, setError] = useState('')
  const load = useCallback(async () => {
    if (!doctorId) return
    try {
      const [monthly, daily] = await Promise.all([
        getReport(doctorId, {
          family,
          start: `${day.slice(0, 7)}-01`,
          end: monthEnd,
          granularity: 'month'
        }),
        family === 'analytics'
          ? getReport(doctorId, {
              family,
              start: day,
              end: day,
              granularity: 'day'
            })
          : Promise.resolve(null)
      ])
      setReport(monthly)
      setTodayReport(daily)
      setError('')
    } catch (e) {
      setError((e as Error).message)
    }
  }, [doctorId, day, monthEnd, family])
  useEffect(() => {
    void load()
    const refresh = () => {
      void load()
    }
    window.addEventListener('clinic-report-change', refresh)
    return () => {
      window.removeEventListener('clinic-report-change', refresh)
    }
  }, [load])
  return (
    <Box bg="white" p={5} borderRadius="xl" borderWidth="1px">
      <Flex align="center" justify="space-between" gap={3}>
        <Heading size="sm">{t(family)}</Heading>
        <Tooltip label={t('openReport')}>
          <IconButton
            as={Link}
            to={family === 'finance' ? '/finances' : '/analytics'}
            aria-label={`${t('openReport')}: ${t(family)}`}
            icon={
              <Icon
                as={ArrowRightIcon}
                boxSize={4}
                transform={
                  locale.startsWith('ar') ? 'rotate(180deg)' : undefined
                }
              />
            }
            size="xs"
            variant="ghost"
            color="gray.500"
          />
        </Tooltip>
      </Flex>
      {family === 'finance' && (
        <Text color="gray.500" fontSize="sm" my={2}>
          {t('thisMonth')}
        </Text>
      )}
      {error ? (
        <Alert status="error">{error}</Alert>
      ) : !report ? (
        <Spinner />
      ) : (
        <>
          {report.migration.status !== 'complete' && (
            <Text fontSize="sm" color="orange.700">
              {t('migrationPending')}
            </Text>
          )}
          {family === 'analytics' ? (
            <SimpleGrid columns={2} gap={5} mt={4}>
              {[
                { label: 'today', data: todayReport },
                { label: 'thisMonth', data: report }
              ].map(({ label, data }, index) => (
                <Box
                  key={label}
                  borderInlineStartWidth={index ? '1px' : 0}
                  borderColor="gray.100"
                  ps={index ? 5 : 0}
                >
                  <Text fontSize="sm" fontWeight="600" color="gray.600" mb={3}>
                    {t(label)}
                  </Text>
                  <Flex gap={4} wrap="wrap">
                    {['visits', 'followUps'].map((key) => (
                      <Box key={key}>
                        <Text fontSize="xs" color="gray.500">
                          {t(key)}
                        </Text>
                        <Text fontSize="2xl" fontWeight="650" mt={1}>
                          {data
                            ? (data.totals[key] ?? 0).toLocaleString(locale)
                            : '—'}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                </Box>
              ))}
            </SimpleGrid>
          ) : (
            <Box my={5}>
              <Text fontSize="xs" color="gray.500">
                {t('billedMinor')}
              </Text>
              <Text fontSize="2xl" fontWeight="650" mt={1}>
                EGP{' '}
                {((report.totals.billedMinor ?? 0) / 100).toLocaleString(
                  locale
                )}
              </Text>
            </Box>
          )}
          {family === 'finance' && !!report.totals.unknownCharges && (
            <Text fontSize="sm">
              {t('unknownCharges')}: {report.totals.unknownCharges ?? 0}
            </Text>
          )}
        </>
      )}
    </Box>
  )
}
